import { useMemo, useState } from "react";
import { Modal } from "flowbite-react";
import { Link, RouteComponentProps } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Button } from "@/components/button";
import MenuPageLayout from "@/components/layouts/MenuPageLayout";
import { queryClient, useResource } from "@/hooks/resources";
import Invitation from "@/types/invitation";
import { request } from "@/utils/request";
import { useAppContext } from "@/contexts/app-context";
import { useIonRouter } from "@ionic/react";

const InvitationPage = ({ match }: RouteComponentProps<{ id: string }>) => {
  const invitationId = match?.params?.id;

  const { state: { selectedChildId, childAuthorizations } } = useAppContext()
  const [invitation] = useResource<Invitation>(`/invitations/${invitationId}`)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalChildId, setModalChildId] = useState<number>(parseInt(selectedChildId as string, 10))
  const [acceptingInvitation, setAcceptingInvitation] = useState(false)
  const router = useIonRouter()

  const isParentInvitation = useMemo(() => {
    if (!invitation) return false

    return invitation.sender.role === "professional"
  }, [invitation])

  const onClickAcceptParentInvitation = () => {
    setModalOpen(true)
  }

  const onSubmitAcceptParentInvitation = async () => {
    setAcceptingInvitation(true)

    await request.post(`/invitations/${invitationId}/accept`, { childId: modalChildId })

    setAcceptingInvitation(false)
  }

  const onClickAcceptProfessionalInvitation = async () => {
    setAcceptingInvitation(true)

    const response = await request.post(`/invitations/${invitationId}/accept`)

    if (response.ok) {
      queryClient.invalidateQueries({ queryKey: ["/invitations"] })
      queryClient.invalidateQueries({ queryKey: ["/child_authorizations"] })

      router.push("/account")
    }

    setAcceptingInvitation(false)
  }

  const handleSelectChildId = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setModalChildId(parseInt(event.target.value, 10))
  }

  return (
    <MenuPageLayout
      title={`Invitation From ${invitation?.sender?.fullName}`}
      titleClassName="lg:w-[unset] px-8"
      className="flex flex-col gap-4"
    >
      <div className="max-w-xl mx-auto flex flex-col items-start gap-4">
        <Link to="/account" className="link flex items-center gap-x-1">
          <FontAwesomeIcon icon="chevron-left" size="sm" fixedWidth />
          Back to Invitations
        </Link>

        {
          !!invitation && (
            isParentInvitation ? (
              <div className="flex flex-col items-start gap-4 border rounded-lg p-4">
                <p>Hello {invitation.recipientName},</p>

                <p>I am excited to invite you to join me, Adam Smith, to try out this new innovative program, Sing and Speak 4 Kids (SS4Kids).</p>

                <p>I believe this online game may help your child in their speech-language development.</p>

                <p>It is designed to be easy and fun, and provides a way for us to collaborate with distance learning and progress reports.</p>

                <Button
                  type="button"
                  className="btn btn-secondary my-4"
                  onClick={onClickAcceptParentInvitation}
                  disabled={!invitation}
                >
                  Accept Invitation
                </Button>

                <p>Let’s team up and have fun with this interactive music-based game.</p>

                <p>All the best,<br />{invitation.sender.fullName}</p>
              </div>
            ) : (
              <div className="flex flex-col items-start gap-4 border rounded-lg p-4">
                <p>Hi {invitation.recipientName},</p>

                <p>We are pleased to announce that you have been invited by <strong>{invitation.sender.fullName}</strong> to use the evidence-based <em>Sing and Speak 4 Kids</em> (SS4Kids) program with their child, <strong>{invitation.child?.firstName}</strong>. This will give you access to their reports and progress, while helping to support their child’s speech-language development.</p>

                <p>This innovative music and game-based program is designed to be easy, fun, and effective when professionals collaborate with parents.</p>

                <p>We are excited to have you join our SS4Kids community!</p>

                <Button
                  type="button"
                  className="btn btn-secondary my-4"
                  onClick={onClickAcceptProfessionalInvitation}
                  disabled={!invitation}
                  loading={acceptingInvitation}
                >
                  Accept Invitation
                </Button>

                <p>If you have any questions to the parent, you may reply directly to them using their email: <a href="mailto:parent@singandspeak4kids.com" className="link">parent@singandspeak4kids.com</a>.</p>

                <p>Any questions about the Sing and Speak 4 Kids Program, please contact us at <a href="mailto:SS4KidsSupport@iqsonics.com" className="link">SS4KidsSupport@iqsonics.com</a>.</p>

                <p>Welcome!<br /><strong>The Sing and Speak 4 Kids Team</strong></p>
              </div>
            )
          )
        }
      </div>

      <Modal
        show={modalOpen}
        position="center"
        onClose={() => setModalOpen(false)}
        popup
      >
        <Modal.Header>
          Accept Invitation
        </Modal.Header>

        <Modal.Body>
          <div className="py-2 flex flex-col items-start gap-4 text-black">
            Select the child you will be accepting this invitation with:

            <select
              className="rounded-lg"
              onChange={handleSelectChildId}
              value={modalChildId}
            >
              <option value="">Select Child</option>
              {childAuthorizations.map((childAuthorization) => (
                <option
                  key={childAuthorization.id}
                  value={childAuthorization.child.id}
                >
                  {childAuthorization.child.fullName}
                </option>
              ))}
            </select>

            <Button
              onClick={onSubmitAcceptParentInvitation}
              className="btn-secondary"
              loading={acceptingInvitation}
            >
              Accept
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </MenuPageLayout>
  );
};

export default InvitationPage;
